/* eslint-disable no-undef */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { jsonResponse, mergeOptions } from '../utils/api-utils';
import config from '../config';
import { getItem } from '../db';
import querystring from 'querystring';
import * as N from './normalize';

/**
 * The admin API client.
 *
 * The server groups queues and devices under the site that owns them
 * (`/sites/:siteId/...`); this app was written against a flat, provider-wide
 * surface. The aggregate reads below fan out over the provider's sites and
 * concatenate, and the writes look the owning site up from `./normalize`'s
 * index, so the redux layer keeps seeing one flat list per entity type.
 *
 * Operations the server deliberately dropped reject via `N.unsupported` rather
 * than firing a request that can only 404 — see the list at the bottom.
 */

const BASE = '/api/adm/v1';

async function api(endpoint: any, options = {}) {
  const token = await getItem('token');
  const opts = mergeOptions(options);
  opts.headers.Authorization = `Bearer ${token}`;
  opts.credentials = 'include';
  // The tenant comes from the token, except for the super admin, which names
  // the provider it is working on. The server ignores the header from anyone
  // else, and it is only ever stored for the super admin (app-init-saga).
  const providerId = await getItem('providerId');
  if (providerId) opts.headers['X-Provider-Id'] = providerId;
  return fetch(`${config.apiHost}${endpoint}`, opts).then(jsonResponse);
}

function get(endpoint: any, options = {}) {
  return api(endpoint, options);
}

function post(endpoint: any, options = {}) {
  return api(endpoint, { method: 'POST', ...options });
}

function patch(endpoint: any, options = {}) {
  return api(endpoint, { method: 'PATCH', ...options });
}

function _delete(endpoint: any, options = {}) {
  return api(endpoint, { method: 'DELETE', ...options });
}

// ---------------------------------------------------------------------------
// Site fan-out
// ---------------------------------------------------------------------------

let sitesPromise: Promise<any[]> | null = null;

/**
 * Drop the memoised site list and the write-routing index.
 *
 * Called at the top of every provider reload: sites are read once per reload
 * and then reused by the four device/queue aggregates, rather than fetched four
 * times over.
 */
export function resetApiCache() {
  sitesPromise = null;
  N.forgetRoutes();
}

async function sites(): Promise<any[]> {
  if (!sitesPromise) {
    sitesPromise = get(`${BASE}/sites`)
      .then((r: any) => (r.data ?? []).map(N.site))
      .catch((e: any) => {
        sitesPromise = null;
        throw e;
      });
  }
  return sitesPromise;
}

/** Read one collection from every site the provider owns and concatenate. */
async function acrossSites(collection: string, normalize: (e: any, s: any) => any) {
  const all = await sites();
  const perSite = await Promise.all(
    all.map(async (s: any) => {
      const r = await get(`${BASE}/sites/${encodeURIComponent(s.siteId)}/${collection}`);
      return (r.data ?? []).map((e: any) => normalize(e, s));
    })
  );
  return perSite.flat();
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export async function fetchUserProfile() {
  const r: any = await get(`${BASE}/users/me`);
  return N.object({ user: N.flat(r.data) });
}

/** Super admin only: every provider, for the switcher. */
export async function fetchServiceProviders() {
  const r: any = await get(`${BASE}/service-providers`);
  return N.list((r.data ?? []).map(N.flat));
}

export async function fetchServiceProvider() {
  const r: any = await get(`${BASE}/service-providers/me`);
  return N.object(N.flat(r.data));
}

export async function fetchServiceProviderUsers() {
  const r: any = await get(`${BASE}/service-providers/me/users`);
  return N.list((r.data ?? []).map(N.flat));
}

export async function fetchServices() {
  const r: any = await get(`${BASE}/services`);
  return N.list((r.data ?? []).map(N.flat));
}

export async function fetchLocations() {
  const r: any = await get(`${BASE}/locations`);
  return N.list((r.data ?? []).map(N.flat));
}

export async function fetchControlBoxes() {
  return N.list(await sites());
}

export async function fetchQueues() {
  return N.list(await acrossSites('queues', N.queue));
}

export async function fetchBooths() {
  return N.list(await acrossSites('booths', N.booth));
}

export async function fetchDisplays() {
  return N.list(await acrossSites('displays', N.display));
}

export async function fetchKiosks() {
  return N.list(await acrossSites('kiosks', N.kiosk));
}

export async function fetchQueueDetail(params: any) {
  const route = N.routeFor(params.id);
  if (!route) return N.unsupported(`fetchQueueDetail(${params.id})`);
  const r: any = await get(
    `${BASE}/sites/${encodeURIComponent(route.siteId)}/queues/${encodeURIComponent(route.nativeId)}`
  );
  const all = await sites();
  const site = all.find((s: any) => s.siteId === route.siteId);
  return N.object({ queue: N.queue(r.data, site) });
}

/**
 * Online state for every device at a site, keyed by uuid.
 *
 * Replaces the old per-uuid `/status/iot-device-status/:uuid` polling: one
 * request per site now covers its booths, displays and kiosks.
 */
export async function fetchSiteDeviceStatus(siteId: string) {
  const r: any = await get(`${BASE}/sites/${encodeURIComponent(siteId)}/device-status`);
  return N.list(r.data ?? []);
}

/** The same, for every site the provider owns. */
export async function fetchDeviceStatuses() {
  const all = await sites();
  const perSite = await Promise.all(
    all.map(async (s: any) => {
      const r: any = await get(`${BASE}/sites/${encodeURIComponent(s.siteId)}/device-status`);
      return r.data ?? [];
    })
  );
  return N.list(perSite.flat());
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export interface SignInData {
  username: string;
  password: string;
}

export async function signin(data: SignInData) {
  return await post(`${BASE}/auth/signin`, { body: JSON.stringify(data) });
}

export async function signout() {
  return await post(`${BASE}/auth/signout`);
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

const enc = encodeURIComponent;

/**
 * Build a request body from a form's values.
 *
 * Only the fields the server actually accepts are sent, and a blank `secret` is
 * dropped: reads never ship a device secret, so the form field arrives empty and
 * posting it back would clear the credential the device authenticates with.
 */
function payload(data: any, fields: string[]) {
  const out: any = {};
  for (const key of fields) {
    const value = data[key];
    if (value === undefined || value === null) continue;
    if (key === 'secret' && String(value).trim() === '') continue;
    out[key] = value;
  }
  return out;
}

const BOOTH_FIELDS = ['name', 'code', 'secret', 'queueIds'];
const DISPLAY_FIELDS = [
  'name',
  'displayType',
  'secret',
  'layoutName',
  'themeName',
  'logoUrl',
  'queueIds',
  'boothIds'
];
const KIOSK_FIELDS = [
  'kioskType',
  'secret',
  'layoutName',
  'themeName',
  'printer',
  'kioskButtons',
  'assignedButtons'
];
const QUEUE_FIELDS = [
  'name',
  'locationId',
  'priority',
  'ticketPrefix',
  'serviceIds',
  'serviceCategoryCode',
  'allowRemoteCheckin',
  'maxCheckinsPerDevice',
  'visibleForMobile',
  'businessHours'
];
const SITE_FIELDS = [
  'name',
  'timeZone',
  'locationId',
  'logLevel',
  'ticketFormat',
  'ticketFormatTemplateCode'
];

/** A device is created at the site its form's control-box picker points to. */
function siteOf(data: any): string | undefined {
  return N.routeFor(data.controlBoxId)?.siteId ?? data.siteId;
}

export async function updateQueue(id: any, data: any) {
  const route = N.routeFor(id) ?? (data?.siteId ? { siteId: data.siteId, nativeId: id } : undefined);
  if (!route) return N.unsupported(`updateQueue(${id})`);
  return await patch(`${BASE}/sites/${enc(route.siteId)}/queues/${enc(route.nativeId)}`, {
    body: JSON.stringify(payload(data, QUEUE_FIELDS))
  });
}

export async function updateControlBox(id: any, data: any) {
  const route = N.routeFor(id);
  if (!route) return N.unsupported(`updateControlBox(${id})`);
  return await patch(`${BASE}/sites/${enc(route.siteId)}`, {
    body: JSON.stringify(payload(data, SITE_FIELDS))
  });
}

export async function sendCbxConfig(id: any) {
  const route = N.routeFor(id);
  if (!route) return N.unsupported(`sendCbxConfig(${id})`);
  return await post(`${BASE}/sites/${enc(route.siteId)}/send-cbx-config`);
}

export async function createService(data: any) {
  return await post(`${BASE}/services`, { body: JSON.stringify(data) });
}

export async function updateService(id: any, data: any) {
  return await patch(`${BASE}/services/${encodeURIComponent(id)}`, { body: JSON.stringify(data) });
}

export async function createLocation(data: any) {
  return await post(`${BASE}/locations`, { body: JSON.stringify(data) });
}

export async function updateLocation(id: any, data: any) {
  return await patch(`${BASE}/locations/${encodeURIComponent(id)}`, { body: JSON.stringify(data) });
}

const DELETABLE: Record<string, string> = {
  Service: 'services',
  Location: 'locations'
};

export async function deleteItem(opts: any) {
  const collection = DELETABLE[opts.itemType];
  if (!collection) return N.unsupported(`delete ${opts.itemType}`);
  return await _delete(`${BASE}/${collection}/${encodeURIComponent(opts.itemId)}`);
}

// ---------------------------------------------------------------------------
// Customers and logs
//
// Written against the server's own shapes (a bare `data` payload), unlike the
// redux code above — these callers are new.
// ---------------------------------------------------------------------------

export async function findCustomerByEmail(email: string) {
  return await get(`${BASE}/customers?email=${encodeURIComponent(email)}`);
}

export async function deleteCustomer(id: any) {
  return await _delete(`${BASE}/customers/${encodeURIComponent(id)}`);
}

export async function fetchLogs(filter: { limit?: number } = {}) {
  return await get(`${BASE}/logs?${querystring.stringify(filter)}`);
}

export async function createControlBox(data: any) {
  return await post(`${BASE}/sites`, {
    body: JSON.stringify({ uuid: data.uuid, ...payload(data, SITE_FIELDS) })
  });
}

export async function createQueue(data: any) {
  const siteId = siteOf(data);
  if (!siteId) return N.unsupported('createQueue (no control box selected)');
  return await post(`${BASE}/sites/${enc(siteId)}/queues`, {
    body: JSON.stringify(payload(data, QUEUE_FIELDS))
  });
}

export async function createBooth(data: any) {
  const siteId = siteOf(data);
  if (!siteId) return N.unsupported('createBooth (no control box selected)');
  return await post(`${BASE}/sites/${enc(siteId)}/booths`, {
    body: JSON.stringify({ uuid: data.uuid, ...payload(data, BOOTH_FIELDS) })
  });
}

export async function updateBooth(id: any, data: any) {
  const route = N.routeFor(id);
  if (!route) return N.unsupported(`updateBooth(${id})`);
  return await patch(`${BASE}/sites/${enc(route.siteId)}/booths/${enc(route.nativeId)}`, {
    body: JSON.stringify(payload(data, BOOTH_FIELDS))
  });
}

export async function createDisplay(data: any) {
  const siteId = siteOf(data);
  if (!siteId) return N.unsupported('createDisplay (no control box selected)');
  return await post(`${BASE}/sites/${enc(siteId)}/displays`, {
    body: JSON.stringify({ uuid: data.uuid, ...payload(data, DISPLAY_FIELDS) })
  });
}

export async function updateDisplay(id: any, data: any) {
  const route = N.routeFor(id);
  if (!route) return N.unsupported(`updateDisplay(${id})`);
  return await patch(`${BASE}/sites/${enc(route.siteId)}/displays/${enc(route.nativeId)}`, {
    body: JSON.stringify(payload(data, DISPLAY_FIELDS))
  });
}

export async function createKiosk(data: any) {
  const siteId = siteOf(data);
  if (!siteId) return N.unsupported('createKiosk (no control box selected)');
  return await post(`${BASE}/sites/${enc(siteId)}/kiosks`, {
    body: JSON.stringify({ uuid: data.uuid, ...payload(data, KIOSK_FIELDS) })
  });
}

export async function updateKiosk(id: any, data: any) {
  const route = N.routeFor(id);
  if (!route) return N.unsupported(`updateKiosk(${id})`);
  return await patch(`${BASE}/sites/${enc(route.siteId)}/kiosks/${enc(route.nativeId)}`, {
    body: JSON.stringify(payload(data, KIOSK_FIELDS))
  });
}

// ---------------------------------------------------------------------------
// Still absent from the server, kept here so callers fail loudly and findably
//
//  - deleting sites, queues and devices: removing one has to account for live
//    tickets and the device index, so it is a deliberate omission rather than
//    an oversight. Services and locations do delete.
//  - ControlBox restart/reload/update and display addressing: there is no
//    on-premise ControlBox process left to command (all 214 boxes are cloud).
//  - kiosk SSH tunnels: the socket-tunnel feature is gone with them.
//  - /status/cbx-queue: no equivalent; queue state ships with the queue.
//
// Signup, impersonation, the provider list and provider deletion had no
// endpoint either, and their callers are gone too: the tenant comes from the
// token, so a session is scoped to one provider and cannot enumerate, switch
// between or delete them.
// ---------------------------------------------------------------------------

export async function sendKioskCommand(_id: any, _commandData: any) {
  return N.unsupported('sendKioskCommand');
}

export async function restartControlBox(_id: any) {
  return N.unsupported('restartControlBox');
}

export async function reloadControlBox(_id: any) {
  return N.unsupported('reloadControlBox');
}

export async function cbxUpdate(_id: any) {
  return N.unsupported('cbxUpdate');
}

export async function configDisplay(_id: any, _address: any) {
  return N.unsupported('configDisplay');
}

export async function fetchCbxQueueStatus() {
  return N.unsupported('fetchCbxQueueStatus');
}
